'use client';
import {
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import en from "../locales/en.json";
import ar from "../locales/ar.json";
import ko from "../locales/ko.json";
import ph from "../locales/ph.json";
import hi from "../locales/hi.json";
import ur from "../locales/ur.json";
import ru from "../locales/ru.json";
import id from "../locales/id.json";
import zh from "../locales/zh.json";
import fr from "../locales/fr.json";
import es from "../locales/es.json";
import it from "../locales/it.json";
import de from "../locales/de.json";

export const SUPPORTED_LANGS = [
  "en",
  "ar",
  "ko",
  "ph",
  "hi",
  "ur",
  "ru",
  "id",
  "zh",
  "fr",
  "es",
  "it",
  "de",
] as const;

export const translations: Record<(typeof SUPPORTED_LANGS)[number], any> = {
  en,
  ar,
  ko,
  ph,
  hi,
  ur,
  ru,
  id,
  zh,
  fr,
  es,
  it,
  de,
};

interface Context {
  lang: (typeof SUPPORTED_LANGS)[number];
  changeLanguage: (l: (typeof SUPPORTED_LANGS)[number]) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<Context>({
  lang: "en",
  changeLanguage: () => {},
  t: (k) => k,
});

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [lang, setLang] = useState<(typeof SUPPORTED_LANGS)[number]>("en");
  const [hydrated, setHydrated] = useState(false);
  const router = useRouter();
  const pathname = usePathname() || "/";

  // determine language from the first path segment
  useEffect(() => {
    const segment = pathname.split("/")[1];
    if (SUPPORTED_LANGS.includes(segment as any)) {
      setLang(segment as (typeof SUPPORTED_LANGS)[number]);
    } else {
      setLang("en");
    }
    setHydrated(true);
  }, [pathname]);

  // update html attributes when lang changes
  useLayoutEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir =
        lang === "ar" || lang === "ur" ? "rtl" : "ltr";
    }
  }, [lang]);

  const changeLanguage = (l: (typeof SUPPORTED_LANGS)[number]) => {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 0) {
      router.push(`/${l}/home`);
      return;
    }
    if (SUPPORTED_LANGS.includes(segments[0] as any)) {
      segments[0] = l;
    } else {
      segments.unshift(l);
    }
    const newPath = "/" + segments.join("/");
    router.push(newPath);
  };

  const t = (key: string): string => {
    const value = translations[lang][key as keyof typeof translations.en];
    return value || key;
  };

  if (!hydrated) return null; // prevent mismatched render during hydration

  return (
    <LanguageContext.Provider value={{ lang, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
